import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ScrollToTop } from './components/ScrollToTop';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';

// Auth Pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { VerifyEmail } from './pages/auth/VerifyEmail';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';

// Public Pages
import { Home } from './pages/Home';
import { RaffleList } from './pages/RaffleList';
import { RaffleDetails } from './pages/RaffleDetails';
import { RaffleCountdown } from './pages/RaffleCountdown';
import { ContactUs } from './pages/ContactUs';
import { FAQ } from './pages/FAQ';
import { TermsOfService } from './pages/TermsOfService';
import { PrivacyPolicy } from './pages/PrivacyPolicy';

// User Pages
import { Dashboard } from './pages/Dashboard';
import { MyTickets } from './pages/tickets/MyTickets';
import { Wallet } from './pages/wallet/Wallet';
import { Invitations } from './pages/invitations/Invitations';
import { Affiliate } from './pages/Affiliate';
import { Settings } from './pages/Settings';
import { PaymentCallback } from './pages/PaymentCallback';
import { DrawHistory } from './pages/DrawHistory';
import { AgentApply } from './pages/agent/AgentApply';
import { AgentStatus } from './pages/agent/AgentStatus';
import { AgentDashboard } from './pages/agent/AgentDashboard';
import { AgentSellTickets } from './pages/agent/AgentSellTickets';
import { AgentSales } from './pages/agent/AgentSales';
import { AgentValidateTicket } from './pages/agent/AgentValidateTicket';
import { AgentProfile } from './pages/agent/AgentProfile';

// Admin Pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminRaffles } from './pages/admin/AdminRaffles';
import { AdminCreateRaffle } from './pages/admin/AdminCreateRaffle';
import { AdminEditRaffle } from './pages/admin/AdminEditRaffle';
import { AdminDrawHistory } from './pages/admin/AdminDrawHistory';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminWithdrawals } from './pages/admin/AdminWithdrawals';
import { AdminConfig } from './pages/admin/AdminConfig';
import { AdminCountryConfig } from './pages/admin/AdminCountryConfig';
import { AdminFinance } from './pages/admin/AdminFinance';
import { AdminAgents } from './pages/admin/AdminAgents';
import { AdminAgentDetail } from './pages/admin/AdminAgentDetail';
import { AdminUserDetail } from './pages/admin/AdminUserDetail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
          <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100 transition-colors">
            <ScrollToTop />
            <Header />
            <main className="flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/raffles" element={<RaffleList />} />
                <Route path="/raffles/:id" element={<RaffleDetails />} />
                <Route path="/raffles/:id/countdown" element={<RaffleCountdown />} />
                <Route path="/draw-history" element={<DrawHistory />} />
                <Route path="/draw-history/:raffleId" element={<DrawHistory />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Payment Callback */}
                <Route path="/payment/callback" element={<PaymentCallback />} />

                {/* Protected User Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
                <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                <Route path="/invitations" element={<ProtectedRoute><Invitations /></ProtectedRoute>} />
                <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/agent/apply" element={<ProtectedRoute><AgentApply /></ProtectedRoute>} />
                <Route path="/agent/status" element={<ProtectedRoute><AgentStatus /></ProtectedRoute>} />
                <Route path="/agent/dashboard" element={<ProtectedRoute requireAgent><AgentDashboard /></ProtectedRoute>} />
                <Route path="/agent/sell" element={<ProtectedRoute requireAgent><AgentSellTickets /></ProtectedRoute>} />
                <Route path="/agent/sales" element={<ProtectedRoute requireAgent><AgentSales /></ProtectedRoute>} />
                <Route path="/agent/validate" element={<ProtectedRoute requireAgent><AgentValidateTicket /></ProtectedRoute>} />
                <Route path="/agent/profile" element={<ProtectedRoute requireAgent><AgentProfile /></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/raffles" element={<ProtectedRoute requireAdmin><AdminRaffles /></ProtectedRoute>} />
                <Route path="/admin/raffles/create" element={<ProtectedRoute requireAdmin><AdminCreateRaffle /></ProtectedRoute>} />
                <Route path="/admin/raffles/:id/edit" element={<ProtectedRoute requireAdmin><AdminEditRaffle /></ProtectedRoute>} />
                <Route path="/admin/raffles/:id/draws" element={<ProtectedRoute requireAdmin><AdminDrawHistory /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/users/:id" element={<ProtectedRoute requireAdmin><AdminUserDetail /></ProtectedRoute>} />
                <Route path="/admin/withdrawals" element={<ProtectedRoute requireAdmin><AdminWithdrawals /></ProtectedRoute>} />
                <Route path="/admin/config" element={<ProtectedRoute requireAdmin><AdminConfig /></ProtectedRoute>} />
                <Route path="/admin/country-config" element={<ProtectedRoute requireAdmin><AdminCountryConfig /></ProtectedRoute>} />
                <Route path="/admin/finance" element={<ProtectedRoute requireAdmin><AdminFinance /></ProtectedRoute>} />
                <Route path="/admin/agents" element={<ProtectedRoute requireAdmin><AdminAgents /></ProtectedRoute>} />
                <Route path="/admin/agents/:id" element={<ProtectedRoute requireAdmin><AdminAgentDetail /></ProtectedRoute>} />

                {/* Legacy redirects */}
                <Route path="/my-raffles" element={<Navigate to="/admin/raffles" replace />} />
                <Route path="/raffles/create" element={<Navigate to="/admin/raffles/create" replace />} />
                <Route path="/raffles/:id/edit" element={<Navigate to="/admin/raffles" replace />} />
                <Route path="/raffles/:id/draws" element={<Navigate to="/admin/raffles" replace />} />

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
          </ThemeProvider>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;